import { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toastr } from "react-redux-toastr";

import { loginThunk } from "@/features/auth/store/authSlice";
import { logoutAction } from "@/features/auth/store/logoutAction";
import {
  authLoadingSelector as isLoadingSelector,
  authErrorSelector as isLoggedErrorSelector,
  isLoggedInSelector,
} from "@/features/auth/store/authSlice";
import { reloadOtherTabs } from "@/shared/helpers/authHelper";
import FullScreenLoader from "@/components/loaders/FullScreenLoader";
import { clearLocal, clearRedirectPath } from "@/helpers/localStore";
import { persistor } from "@/config/store";
import { redirectToUrl } from "@/shared/helpers/tokenBasedLoginHelper";
import { getDashboardPath } from "@/helpers/utils";
import { logger } from "@/helpers/loggerHelper";
import { ROUTES } from "@/helpers/routes/routePaths";
import { getSafeErrorLog } from "@/helpers/errorHandler";

type AuthRedirectState = {
  auth: {
    error?: unknown;
    isUserLoggedIn?: boolean;
    loading?: boolean;
  };
};

type DispatchAction = (_action: unknown) => Promise<unknown> | unknown;

function LoginRedirect() {
  const navigate = useNavigate();
  const location = useLocation();

  const dispatch = useDispatch() as DispatchAction;
  const isLoggedIn = useSelector((state: AuthRedirectState) =>
    isLoggedInSelector(state)
  );
  const isLoading = useSelector((state: AuthRedirectState) =>
    isLoadingSelector(state)
  );
  const isLoggedError = useSelector((state: AuthRedirectState) =>
    isLoggedErrorSelector(state)
  );

  const [hasLoggedOut, setHasLoggedOut] = useState(false);

  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");

  // Step 1: Logout if already logged in

  const handleLogout = useCallback(async () => {
    try {
      await dispatch(logoutAction());
      await persistor.purge();
      clearLocal();
      logger.info("Logging out..");
      setHasLoggedOut(true);
    } catch (error) {
      logger.error("Logout failed:", error);
    }
  }, [dispatch]);

  const handleLogin = useCallback(
    async (token: string) => {
      try {
        await dispatch(loginThunk({ accessToken: token }));
        reloadOtherTabs();
        logger.info("Logged In...");
      } catch (error) {
        logger.error("Login failed:", getSafeErrorLog(error));
        // redirect to login when login with token failed
        toastr.error("Error", "Invalid token");
        navigate(ROUTES.ROOT, { replace: true });
      }
    },
    [dispatch, navigate]
  );

  useEffect(() => {
    if (token && isLoggedIn && !hasLoggedOut) {
      handleLogout();
    } else if (token && !isLoggedIn && !hasLoggedOut) {
      setHasLoggedOut(true);
    }
  }, [dispatch, token, isLoggedIn, hasLoggedOut, handleLogout]);

  // Step 2: Login with token

  useEffect(() => {
    if (token && hasLoggedOut && !isLoggedIn) {
      handleLogin(token);
    }
  }, [dispatch, token, isLoggedIn, hasLoggedOut, handleLogin]);

  useEffect(() => {
    if (isLoggedError) {
      navigate(ROUTES.ROOT, { replace: true });
    }
  }, [isLoggedError, navigate]);

  // Step 3: Redirect after successful login
  useEffect(() => {
    if (token && hasLoggedOut && isLoggedIn && !isLoading) {
      logger.info("Redirecting to dashboard...");
      reloadOtherTabs();
      clearRedirectPath();
      redirectToUrl(getDashboardPath());
    } else if (!token) {
      navigate(ROUTES.ROOT, { replace: true });
    }
  }, [token, isLoggedIn, hasLoggedOut, isLoading, navigate]);

  return <FullScreenLoader message={"Redirecting..."} />;
}

export default LoginRedirect;
